import type { FaultData } from '../api/types';
import { formatDTCCode, getFaultSeverity, getFaultStateLabel, faultLabel } from '../utils/dtc';
import { escapeHtml } from '../utils/html';

/** HTML for a single vehicle/ECU fault card (critical styling). */
export function buildVehicleFaultCardHTML(fault: FaultData): string {
  const dtc = formatDTCCode(fault.diagnostic?.code ?? null);
  const name = fault.diagnostic?.name;
  const isUnknownName = !name || name.toLowerCase().includes('unknown') || name.startsWith('**');
  const severity  = getFaultSeverity(fault);
  const stateInfo = getFaultStateLabel(fault);
  const hasEnrichment = fault.faultDescription || fault.effectOnComponent || fault.recommendation;
  const triggerCount = fault.count ?? 1;

  // Top row: code + name on the left, status badges on the right
  let h = `<div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:0.5rem;">
    <div style="min-width:0;flex:1;">
      ${dtc ? `<div style="font-family:JetBrains Mono,monospace;font-size:1.15rem;font-weight:700;color:var(--critical);">${dtc}</div>` : ''}
      <div style="font-family:JetBrains Mono,monospace;font-size:0.8rem;color:rgba(239,68,68,0.75);margin-top:0.15rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
        ${escapeHtml(!isUnknownName ? name : (dtc ? 'OEM proprietary — scanner required' : 'Unknown Fault'))}
      </div>
    </div>
    <div style="display:flex;flex-direction:column;gap:0.3rem;align-items:flex-end;flex-shrink:0;margin-left:0.75rem;">
      ${severity  ? `<span style="font-size:0.65rem;font-weight:700;background:${severity.bg};color:${severity.color};padding:0.2rem 0.5rem;border-radius:4px;">${severity.label}</span>` : ''}
      ${stateInfo ? `<span style="font-size:0.65rem;font-weight:600;color:${stateInfo.color};">${stateInfo.label}</span>` : ''}
    </div>
  </div>`;

  // Trigger count bar — prominent, full-width, only shown when > 1
  const triggerBar = triggerCount > 1
    ? `<div style="display:flex;align-items:center;gap:0.6rem;background:rgba(239,68,68,0.12);border:1px solid rgba(239,68,68,0.25);border-radius:6px;padding:0.45rem 0.75rem;margin-bottom:0.6rem;">
        <span style="font-size:1.1rem;font-weight:800;color:var(--critical);font-family:JetBrains Mono,monospace;">×${triggerCount.toLocaleString()}</span>
        <span style="font-size:0.8rem;font-weight:600;color:rgba(239,68,68,0.85);">triggers in last 30 days</span>
      </div>`
    : '';

  let e = '';
  if (hasEnrichment) {
    e += `<div style="margin-top:0.75rem;padding-top:0.75rem;border-top:1px solid rgba(239,68,68,0.15);">`;
    if (fault.faultDescription) {
      e += `<div style="font-size:0.8rem;color:var(--text);margin-bottom:0.5rem;">${escapeHtml(fault.faultDescription)}</div>`;
    }
    if (fault.effectOnComponent) {
      e += `<div style="margin-bottom:0.5rem;"><div style="font-size:0.65rem;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:0.2rem;">Effect on Component</div><div style="font-size:0.8rem;color:var(--text);">${escapeHtml(fault.effectOnComponent)}</div></div>`;
    }
    if (fault.recommendation) {
      e += `<div style="background:rgba(59,130,246,0.08);border-left:2px solid var(--accent);padding:0.5rem 0.75rem;border-radius:4px;"><div style="font-size:0.65rem;font-weight:700;color:var(--accent);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:0.2rem;">Recommended Action</div><div style="font-size:0.8rem;color:var(--text);">${escapeHtml(fault.recommendation)}</div></div>`;
    }
    e += `</div>`;
  }
  if (fault.riskOfBreakdown) {
    e += `<div style="margin-top:0.4rem;font-size:0.75rem;color:var(--muted);">Risk of breakdown: <span style="color:var(--critical);font-weight:600;">${escapeHtml(fault.riskOfBreakdown)}</span></div>`;
  }
  e += `<div style="font-size:0.7rem;color:var(--muted);margin-top:0.5rem;">Last triggered: ${new Date(fault.dateTime).toLocaleString()}</div>`;

  // Trigger log — native <details> expand, shows last 50 dated events
  let log = '';
  const dates = fault.triggerDates ?? [];
  if (dates.length > 0) {
    const totalCount = triggerCount;
    const showing = dates.length;
    const caption = showing < totalCount
      ? `Showing last ${showing} of ${totalCount.toLocaleString()} triggers (last 30 days)`
      : `All ${totalCount.toLocaleString()} trigger${totalCount > 1 ? 's' : ''} in last 30 days`;

    const rows = dates.map((d) => {
      const dt = new Date(d);
      return `<div style="display:flex;justify-content:space-between;padding:0.3rem 0;border-bottom:1px solid rgba(239,68,68,0.08);font-size:0.72rem;color:var(--text);">
        <span>${dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        <span style="color:var(--muted);font-family:JetBrains Mono,monospace;">${dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
      </div>`;
    }).join('');

    log = `<details style="margin-top:0.6rem;">
      <summary style="cursor:pointer;font-size:0.75rem;font-weight:600;color:rgba(239,68,68,0.8);user-select:none;list-style:none;display:flex;align-items:center;gap:0.4rem;">
        <span style="font-size:0.7rem;">▸</span> View trigger log
      </summary>
      <div style="margin-top:0.5rem;background:rgba(0,0,0,0.15);border-radius:6px;padding:0.5rem 0.75rem;max-height:220px;overflow-y:auto;">
        <div style="font-size:0.65rem;color:var(--muted);margin-bottom:0.4rem;">${caption}</div>
        ${rows}
      </div>
    </details>`;
  }

  return `<div style="background:rgba(239,68,68,0.08);border-left:3px solid var(--critical);padding:1rem;border-radius:8px;margin-bottom:0.75rem;">${h}${triggerBar}${e}${log}</div>`;
}

/** HTML for a single telematics/device notification card (muted styling). */
export function buildTelematicsFaultCardHTML(fault: FaultData): string {
  const stateInfo = getFaultStateLabel(fault);
  return `<div style="background:rgba(139,149,168,0.06);border-left:3px solid var(--muted);padding:0.875rem;border-radius:8px;margin-bottom:0.6rem;opacity:0.8;">
    <div style="display:flex;align-items:center;gap:0.5rem;margin-bottom:0.3rem;justify-content:space-between;">
      <div style="display:flex;align-items:center;gap:0.5rem;">
        <span style="font-size:0.65rem;font-weight:700;background:rgba(139,149,168,0.2);color:var(--muted);padding:0.1rem 0.4rem;border-radius:4px;text-transform:uppercase;letter-spacing:0.5px;">Info</span>
        <span style="font-family:JetBrains Mono,monospace;font-size:0.85rem;color:var(--muted);">${escapeHtml(faultLabel(fault))}</span>
      </div>
      ${stateInfo ? `<span style="font-size:0.65rem;font-weight:600;color:${stateInfo.color};flex-shrink:0;">${stateInfo.label}</span>` : ''}
    </div>
    <div style="font-size:0.8rem;color:var(--muted);opacity:0.7;">Failure Mode: ${escapeHtml(fault.failureMode?.name ?? 'Unknown')}</div>
    <div style="font-size:0.7rem;color:var(--muted);opacity:0.6;margin-top:0.2rem;">Detected: ${new Date(fault.dateTime).toLocaleString()}</div>
  </div>`;
}
